import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useFreemium } from "@/hooks/useFreemium";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import TableFilterBar from "@/components/table/TableFilterBar";
import TableSettingsGear, { TableColumnsPanel } from "@/components/table/TableSettingsGear";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  CalendarCheck, Plus, Trash2, MoreHorizontal, Eye, QrCode,
  MapPin, Clock, CalendarDays, ChevronLeft, ChevronRight, LayoutGrid, List, ArrowUpDown, ArrowUp, ArrowDown, Pencil, History, Share2, Loader2, ImageIcon, Copy, Mail, Link, Code2, Search, SlidersHorizontal, Check, X,
} from "lucide-react";
import EventFormDialog from "@/components/attendance/EventFormDialog";
import QRCodeDialog from "@/components/attendance/QRCodeDialog";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfYear, endOfYear, eachDayOfInterval, eachMonthOfInterval, isSameMonth, isSameDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, addYears, subYears, isAfter, isBefore, startOfDay, parseISO } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { QRCodeSVG } from "qrcode.react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";

interface Event {
  id: string;
  name: string;
  event_type: string;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  is_active: boolean;
  notes: string | null;
  description: string | null;
  location: string | null;
  flyer_url: string | null;
  category: string;
  public_view_enabled: boolean;
  created_at: string;
}

type SortDir = "asc" | "desc" | null;
type EventColumnKey = "name" | "event_type" | "category" | "event_date" | "start_time" | "location" | "attendees";

interface EventColumnDef {
  key: EventColumnKey;
  label: string;
  sortField: EventColumnKey;
  render: (event: Event, totalAttendees: number) => React.ReactNode;
}

const CATEGORY_COLORS: Record<string, string> = {
  service: "bg-primary/10 text-primary border-primary/20",
  special: "bg-accent/10 text-accent border-accent/20",
  youth: "bg-info/80 text-info-foreground",
  prayer: "bg-purple/10 text-purple border-purple/20",
  outreach: "bg-success/10 text-success border-success/20",
};

const MINISTRY_CALENDAR_TONES = [
  { surface: "bg-blue-100 text-blue-900 border-blue-400", bar: "bg-blue-500", dot: "bg-blue-500" },
  { surface: "bg-emerald-100 text-emerald-900 border-emerald-400", bar: "bg-emerald-500", dot: "bg-emerald-500" },
  { surface: "bg-amber-100 text-amber-900 border-amber-400", bar: "bg-amber-500", dot: "bg-amber-500" },
  { surface: "bg-rose-100 text-rose-900 border-rose-400", bar: "bg-rose-500", dot: "bg-rose-500" },
  { surface: "bg-indigo-100 text-indigo-900 border-indigo-400", bar: "bg-indigo-500", dot: "bg-indigo-500" },
  { surface: "bg-cyan-100 text-cyan-900 border-cyan-400", bar: "bg-cyan-500", dot: "bg-cyan-500" },
  { surface: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-400", bar: "bg-fuchsia-500", dot: "bg-fuchsia-500" },
  { surface: "bg-lime-100 text-lime-900 border-lime-400", bar: "bg-lime-500", dot: "bg-lime-500" },
];

const getMinistryCalendarTone = (ministry: string | null | undefined) => {
  const normalized = (ministry || "General").trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) hash = (hash * 31 + normalized.charCodeAt(i)) >>> 0;
  return MINISTRY_CALENDAR_TONES[hash % MINISTRY_CALENDAR_TONES.length];
};

const CATEGORY_LABELS: Record<string, string> = {
  service: "Service",
  special: "Special Event",
  youth: "Youth",
  prayer: "Prayer",
  outreach: "Outreach",
};

const MINISTRY_NOTE_PREFIX = "Ministry: ";
const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_NOTE_PREFIX = "Organization: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";

const getOrganizationFromNotes = (notes: string | null) => {
  if (!notes) return "";
  const firstLine = notes.split("\n")[0]?.trim() || "";
  if (firstLine.startsWith(MINISTRY_NOTE_PREFIX)) return firstLine.replace(MINISTRY_NOTE_PREFIX, "").trim();
  if (firstLine.startsWith(LEGACY_ORG_NOTE_PREFIX)) return firstLine.replace(LEGACY_ORG_NOTE_PREFIX, "").trim();
  return "";
};

const getOrganizationIdsFromNotes = (notes: string | null) => {
  if (!notes) return [] as string[];
  const lines = notes.split("\n").map((line) => line.trim());
  const idsLine = lines.find((line) =>
    line.startsWith(MINISTRY_IDS_NOTE_PREFIX) || line.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
  );
  if (!idsLine) return [] as string[];
  return idsLine
    .replace(MINISTRY_IDS_NOTE_PREFIX, "")
    .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
    .split(">")
    .map((id) => id.trim())
    .filter(Boolean);
};

const getDisplayNotes = (notes: string | null) => {
  if (!notes) return "";
  return notes
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith(MINISTRY_IDS_NOTE_PREFIX))
    .join("\n")
    .trim();
};

type OrganizationNode = {
  id: string;
  name: string;
  parent_id: string | null;
};

export default function EventsPage() {
  const { profile } = useChurch();
  const { role } = useAuth();
  const { getEventsOrganizationIds, hasEventsScopedAccess } = usePermissions();
  const { isPreview, requireSubscription } = useFreemium();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [editEvent, setEditEvent] = useState<Event | null>(null);
  const [qrEvent, setQrEvent] = useState<{ id: string; name: string } | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarView, setCalendarView] = useState<"day" | "week" | "month" | "year">("week");
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "upcoming");
  const [popoverEvent, setPopoverEvent] = useState<Event | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [calendarTypeFilterOpen, setCalendarTypeFilterOpen] = useState(false);
  const [sharingEvent, setSharingEvent] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareEvent, setShareEvent] = useState<Event | null>(null);
  const [sortColumn, setSortColumn] = useState<EventColumnKey>("event_date");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [categoryFilters, setCategoryFilters] = useState<Set<string>>(new Set());
  const [typeFilters, setTypeFilters] = useState<Set<string>>(new Set());
  const [dateRangeStart, setDateRangeStart] = useState<Date | undefined>(undefined);
  const [dateRangeEnd, setDateRangeEnd] = useState<Date | undefined>(undefined);
  const [evtPageSize, setEvtPageSize] = useState(20);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [createPreFill, setCreatePreFill] = useState<{ date: string; time: string } | null>(null);
  const allowedEventOrgIds = getEventsOrganizationIds();
  const eventsScoped = role !== "admin" && hasEventsScopedAccess();
  const selectedEventId = searchParams.get("eventId");
  const isMobile = useIsMobile();

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["events", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("church_id", profile!.id)
        .order("event_date", { ascending: false });
      if (error) throw error;
      return ((data || []) as any[]).map((event) => ({
        ...event,
        public_view_enabled: event.public_view_enabled ?? true,
      })) as Event[];
    },
    enabled: !!profile?.id,
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "events_scope"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, parent_id");
      if (error) throw error;
      return (data || []) as OrganizationNode[];
    },
  });

  // Attendance counts per event
  const { data: eventCounts = {} } = useQuery({
    queryKey: ["event_counts", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from("attendance_records").select("event_id, is_visitor, member_name").eq("church_id", profile!.id);
      if (error) throw error;
      const counts: Record<string, { members: number; visitors: number; names: string[] }> = {};
      (data || []).forEach((r: any) => {
        if (!counts[r.event_id]) counts[r.event_id] = { members: 0, visitors: 0, names: [] };
        if (r.is_visitor) counts[r.event_id].visitors++; else counts[r.event_id].members++;
        if (r.member_name && !r.is_visitor) counts[r.event_id].names.push(r.member_name);
      });
      return counts;
    },
    enabled: !!profile?.id,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("events").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["event_counts"] });
      
      setDeleteId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const publicViewMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("events")
        .update({ public_view_enabled: enabled })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      
      setSelectedEvent((prev) => (prev && prev.id === vars.id ? { ...prev, public_view_enabled: vars.enabled } : prev));
      setShareEvent((prev) => (prev && prev.id === vars.id ? { ...prev, public_view_enabled: vars.enabled } : prev));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const scopedOrgSet = useMemo(() => {
    if (!eventsScoped) return null;

    const byParent = new Map<string, string[]>();
    organizations.forEach((org) => {
      if (!org.parent_id) return;
      const current = byParent.get(org.parent_id) || [];
      current.push(org.id);
      byParent.set(org.parent_id, current);
    });

    const visited = new Set<string>();
    const queue = [...allowedEventOrgIds];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const children = byParent.get(currentId) || [];
      children.forEach((childId) => {
        if (!visited.has(childId)) queue.push(childId);
      });
    }

    return visited;
  }, [eventsScoped, organizations, allowedEventOrgIds]);

  const organizationNameToIds = useMemo(() => {
    const map = new Map<string, string[]>();
    organizations.forEach((org) => {
      const key = org.name.toLowerCase();
      const existing = map.get(key) || [];
      existing.push(org.id);
      map.set(key, existing);
    });
    return map;
  }, [organizations]);

  const scopedEvents = useMemo(() => {
    if (!eventsScoped || !scopedOrgSet) return events;

    return events.filter((event) => {
      const orgIdsFromNotes = getOrganizationIdsFromNotes(event.notes);
      if (orgIdsFromNotes.length > 0) {
        return orgIdsFromNotes.some((orgId) => scopedOrgSet.has(orgId));
      }

      const orgPath = getOrganizationFromNotes(event.notes);
      if (!orgPath) return false;
      const orgNames = orgPath.split(">").map((part) => part.trim().toLowerCase()).filter(Boolean);
      if (orgNames.length === 0) return false;

      return orgNames.some((name) => {
        const matchedIds = organizationNameToIds.get(name) || [];
        return matchedIds.some((id) => scopedOrgSet.has(id));
      });
    });
  }, [events, eventsScoped, scopedOrgSet, organizationNameToIds]);

  const today = startOfDay(new Date());
  const uniqueCategories = useMemo(
    () => [...new Set(scopedEvents.map((e) => CATEGORY_LABELS[e.category] || e.category))].filter(Boolean).sort(),
    [scopedEvents]
  );
  const uniqueEventTypes = useMemo(
    () => [...new Set(scopedEvents.map((e) => e.event_type || "—"))].filter(Boolean).sort(),
    [scopedEvents]
  );

  const toggleCategoryFilter = (value: string) => {
    setCategoryFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const toggleTypeFilter = (value: string) => {
    setTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return scopedEvents.filter((event) => {
      const eventDay = startOfDay(parseISO(event.event_date));
      const matchesSearch =
        !q ||
        event.name.toLowerCase().includes(q) ||
        event.event_type.toLowerCase().includes(q) ||
        (event.location?.toLowerCase().includes(q) ?? false);
      const matchesType = typeFilters.size === 0 || typeFilters.has(event.event_type || "—");
      const matchesDateStart = !dateRangeStart || !isBefore(eventDay, startOfDay(dateRangeStart));
      const matchesDateEnd = !dateRangeEnd || !isAfter(eventDay, startOfDay(dateRangeEnd));
      return matchesSearch && matchesType && matchesDateStart && matchesDateEnd;
    });
  }, [scopedEvents, search, typeFilters, dateRangeStart, dateRangeEnd]);

  const toggleSort = (field: EventColumnKey) => {
    if (sortColumn === field) {
      if (sortDir === "asc") setSortDir("desc");
      else if (sortDir === "desc") {
        setSortDir(null);
      } else setSortDir("asc");
    } else {
      setSortColumn(field);
      setSortDir("asc");
    }
  };

  const sortEvents = (list: Event[]) => {
    if (!sortColumn || !sortDir) return list;

    return [...list].sort((a, b) => {
      let cmp = 0;

      if (sortColumn === "event_date") {
        cmp = parseISO(a.event_date).getTime() - parseISO(b.event_date).getTime();
      } else if (sortColumn === "start_time") {
        cmp = (a.start_time || "").localeCompare(b.start_time || "");
      } else if (sortColumn === "attendees") {
        const aCount = (eventCounts[a.id]?.members || 0) + (eventCounts[a.id]?.visitors || 0);
        const bCount = (eventCounts[b.id]?.members || 0) + (eventCounts[b.id]?.visitors || 0);
        cmp = aCount - bCount;
      } else if (sortColumn === "category") {
        const aLabel = CATEGORY_LABELS[a.category] || a.category;
        const bLabel = CATEGORY_LABELS[b.category] || b.category;
        cmp = aLabel.localeCompare(bLabel, undefined, { sensitivity: "base" });
      } else {
        const aValue = String((a as any)[sortColumn] || "");
        const bValue = String((b as any)[sortColumn] || "");
        cmp = aValue.localeCompare(bValue, undefined, { sensitivity: "base" });
      }

      return sortDir === "asc" ? cmp : -cmp;
    });
  };

  const upcoming = sortEvents(filtered.filter((event) => !isBefore(parseISO(event.event_date), today)));
  const past = sortEvents(filtered.filter((event) => isBefore(parseISO(event.event_date), today)));

  const [visibleColumns, setVisibleColumns] = useState<Set<EventColumnKey>>(
    new Set(["name", "event_type", "event_date", "start_time", "location", "attendees"])
  );

  const toggleColumn = (key: EventColumnKey) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else next.add(key);
      return next;
    });
  };

  const SortIcon = ({ field }: { field: EventColumnKey }) => {
    if (sortColumn !== field || !sortDir) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 group-hover/th:opacity-40 transition-opacity" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const columns: EventColumnDef[] = [
    {
      key: "name",
      label: "Event",
      sortField: "name",
      render: (event) => (
        <button onClick={() => openEventDetail(event)} className="font-medium text-foreground hover:underline text-left flex items-center gap-2">
          {event.flyer_url ? (
            <img src={event.flyer_url} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
          ) : (
            <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center shrink-0">
              <ImageIcon className="w-4 h-4 text-primary/50" />
            </div>
          )}
          {event.name}
        </button>
      ),
    },
    {
      key: "event_type",
      label: "Type",
      sortField: "event_type",
      render: (event) => <span className="text-sm text-muted-foreground">{event.event_type}</span>,
    },
    {
      key: "event_date",
      label: "Date",
      sortField: "event_date",
      render: (event) => <span className="text-sm text-muted-foreground">{format(parseISO(event.event_date), "MMM d, yyyy")}</span>,
    },
    {
      key: "start_time",
      label: "Time",
      sortField: "start_time",
      render: (event) => (
        <span className="text-sm text-muted-foreground">
          {event.start_time ? `${event.start_time.slice(0, 5)}${event.end_time ? ` – ${event.end_time.slice(0, 5)}` : ""}` : "—"}
        </span>
      ),
    },
    {
      key: "location",
      label: "Location",
      sortField: "location",
      render: (event) => <span className="text-sm text-muted-foreground truncate block max-w-[260px]">{event.location || "—"}</span>,
    },
    {
      key: "attendees",
      label: "Attendees",
      sortField: "attendees",
      render: (_event, totalAttendees) => <Badge variant="secondary">{totalAttendees}</Badge>,
    },
  ];

  const activeColumns = columns.filter((column) => visibleColumns.has(column.key));

  const openEventDetail = (event: Event) => {
    setSelectedEvent(event);
    const next = new URLSearchParams(searchParams);
    next.set("eventId", event.id);
    setSearchParams(next, { replace: true });
  };

  const closeEventDetail = () => {
    setSelectedEvent(null);
    if (!selectedEventId) return;
    const next = new URLSearchParams(searchParams);
    next.delete("eventId");
    setSearchParams(next, { replace: true });
  };

  const publicEventUrl = (event: Pick<Event, "id">) => `${window.location.origin}/event?id=${event.id}`;

  useEffect(() => {
    if (!selectedEventId) return;
    const eventFromQuery =
      scopedEvents.find((event) => event.id === selectedEventId) ||
      events.find((event) => event.id === selectedEventId);
    if (eventFromQuery && selectedEvent?.id !== eventFromQuery.id) {
      setSelectedEvent(eventFromQuery);
    }
  }, [selectedEventId, scopedEvents, events, selectedEvent?.id]);

  const shareEventCheckIn = async (event: Event) => {
    const checkinUrl = `${window.location.origin}/checkin?event_id=${event.id}`;
    const qrSvg = document.getElementById("event-qr-share-svg") as SVGElement | null;

    try {
      setSharingEvent(true);

      let fileToShare: File | null = null;
      if (qrSvg) {
        const svgData = new XMLSerializer().serializeToString(qrSvg);
        const canvas = document.createElement("canvas");
        canvas.width = 1024;
        canvas.height = 1024;
        const ctx = canvas.getContext("2d");
        const img = new Image();

        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            if (!ctx) {
              reject(new Error("Canvas context unavailable"));
              return;
            }
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, 1024, 1024);
            ctx.drawImage(img, 112, 112, 800, 800);
            resolve();
          };
          img.onerror = () => reject(new Error("Unable to render QR image"));
          img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
        });

        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
        if (blob) {
          const fileName = `${event.name.replace(/\s+/g, "-").toLowerCase()}-checkin-qr.png`;
          fileToShare = new File([blob], fileName, { type: "image/png" });
        }
      }

      const shareTitle = `${event.name} QR Check-in`;
      const shareText = `Use this link to sign in for ${event.name}: ${checkinUrl}`;

      if (navigator.share) {
        if (fileToShare && navigator.canShare?.({ files: [fileToShare] })) {
          await navigator.share({ title: shareTitle, text: shareText, url: checkinUrl, files: [fileToShare] });
          return;
        }

        await navigator.share({ title: shareTitle, text: shareText, url: checkinUrl });
        return;
      }

      await navigator.clipboard.writeText(checkinUrl);
      
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        toast.error("Unable to share event check-in");
      }
    } finally {
      setSharingEvent(false);
    }
  };

  // Calendar
  const monthStart = startOfMonth(calendarMonth);
  const monthEnd = endOfMonth(calendarMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = monthStart.getDay();
  const eventsByDate = useMemo(() => {
    const map: Record<string, Event[]> = {};
    scopedEvents.forEach(e => {
      const d = e.event_date;
      if (!map[d]) map[d] = [];
      map[d].push(e);
    });
    return map;
  }, [scopedEvents]);

  const EventCard = ({ event }: { event: Event }) => {
    const c = eventCounts[event.id];
    const total = c ? c.members + c.visitors : 0;

    return (
      <Card className="group hover:shadow-lg transition-all duration-300 cursor-pointer" onClick={() => openEventDetail(event)}>
        {event.flyer_url && (
          <div className="relative h-40 overflow-hidden rounded-t-2xl">
            <img src={event.flyer_url} alt={event.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
        )}
        <CardHeader className={event.flyer_url ? "pt-3 pb-2" : "pb-2"}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-sm font-semibold truncate">{event.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <Badge variant="secondary" className="text-[10px]">{event.event_type}</Badge>
              </div>
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 rounded hover:bg-secondary transition-colors">
                    <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={!event.public_view_enabled} onClick={() => window.open(`/event?id=${event.id}`, "_blank")}>
                    <Eye className="w-4 h-4 mr-2" /> View Public Page
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled={!event.public_view_enabled} onClick={() => {
                    setShareEvent(event);
                    setLinkCopied(false);
                  }}>
                    <Share2 className="w-4 h-4 mr-2" /> Share
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    if (!requireSubscription()) return;
                    setEditEvent(event);
                    setShowCreate(true);
                  }}>
                    <Pencil className="w-4 h-4 mr-2" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    if (!requireSubscription()) return;
                    setDeleteId(event.id);
                  }} className="text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-1.5">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5" />
              {format(parseISO(event.event_date), "MMM d, yyyy")}
            </span>
            {event.start_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {event.start_time.slice(0, 5)}
                {event.end_time && ` – ${event.end_time.slice(0, 5)}`}
              </span>
            )}
          </div>
          {event.location && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3.5 h-3.5" /> {event.location}
            </div>
          )}
          {total > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <div className="flex -space-x-2">
                {(c?.names || []).slice(0, 4).map((name, i) => (
                  <div key={i} className="w-7 h-7 rounded-full bg-primary/15 border-2 border-background flex items-center justify-center text-[10px] font-semibold text-primary">
                    {name.split(" ").map((n: string) => n[0]).join("").slice(0, 2)}
                  </div>
                ))}
                {total > 4 && (
                  <div className="w-7 h-7 rounded-full bg-secondary border-2 border-background flex items-center justify-center text-[10px] font-semibold text-muted-foreground">
                    +{total - 4}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{total} attending</p>
            </div>
          )}
          {total === 0 && <p className="text-xs text-muted-foreground mt-1">No attendees yet</p>}
        </CardContent>
      </Card>
    );
  };

  const EmptyState = ({ message }: { message: string }) => (
    <div className="stat-card text-center py-12">
      <CalendarCheck className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
      <p className="text-muted-foreground text-sm">{message}</p>
    </div>
  );

  const renderEventsList = (list: Event[], emptyMessage: string, page: number, setPage: React.Dispatch<React.SetStateAction<number>>) => {
    if (list.length === 0) return <EmptyState message={emptyMessage} />;

    const totalPages = Math.max(1, Math.ceil(list.length / evtPageSize));
    const adjPage = Math.min(page, totalPages);
    const pagedList = list.slice((adjPage - 1) * evtPageSize, adjPage * evtPageSize);

    if (viewMode === "cards") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pagedList.map((event) => <EventCard key={event.id} event={event} />)}
        </div>
      );
    }

    return (
      <>
        <div className="space-y-3 md:hidden">
          {pagedList.map((event) => {
            const total = (eventCounts[event.id]?.members || 0) + (eventCounts[event.id]?.visitors || 0);
            return (
              <div key={event.id} className="stat-card p-4 cursor-pointer active:scale-[0.99] transition-transform" onClick={() => openEventDetail(event)}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">{event.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{format(parseISO(event.event_date), "MMM d, yyyy")}</p>
                    <p className="text-xs text-muted-foreground">{event.start_time ? event.start_time.slice(0, 5) : "—"}</p>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-secondary transition-colors">
                          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          if (!requireSubscription()) return;
                          setEditEvent(event);
                          setShowCreate(true);
                        }}>
                          <Pencil className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          if (!requireSubscription()) return;
                          setDeleteId(event.id);
                        }} className="text-destructive focus:text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="mt-2">
                  <Badge variant="secondary">{total} attendees</Badge>
                </div>
              </div>
            );
          })}
        </div>

        <div className="stat-card p-0 overflow-x-auto hidden md:block hover:shadow-none hover:border-border hover:translate-y-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                {activeColumns.map((column) => (
                  <th
                    key={column.key}
                    className="group/th text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => toggleSort(column.sortField)}
                  >
                    <span className="inline-flex items-center">
                      {column.label}
                      <SortIcon field={column.sortField} />
                    </span>
                  </th>
                ))}
                <th className="w-20 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                  <TableSettingsGear
                    columns={columns.map((column) => ({ key: column.key, label: column.label }))}
                    visibleColumns={visibleColumns as Set<string>}
                    onToggle={(key) => toggleColumn(key as EventColumnKey)}
                    open={columnsOpen}
                    onOpenChange={setColumnsOpen}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedList.map((event) => {
                const totalAttendees = (eventCounts[event.id]?.members || 0) + (eventCounts[event.id]?.visitors || 0);
                return (
                  <tr key={event.id} className="border-b border-border/50 hover:bg-primary/10 hover:shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.18)] transition-colors cursor-pointer" onClick={() => openEventDetail(event)}>
                    {activeColumns.map((column) => (
                      <td key={column.key} className="px-4 py-3 max-w-[280px] truncate">{column.render(event, totalAttendees)}</td>
                    ))}
                    <td className="px-4 py-3 text-right w-12" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded hover:bg-secondary transition-colors">
                            <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem disabled={!event.public_view_enabled} onClick={() => window.open(`/event?id=${event.id}`, "_blank")}>
                            <Eye className="w-4 h-4 mr-2" /> View Public Page
                          </DropdownMenuItem>
                          <DropdownMenuItem disabled={!event.public_view_enabled} onClick={() => {
                            setShareEvent(event);
                            setLinkCopied(false);
                          }}>
                            <Share2 className="w-4 h-4 mr-2" /> Share
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            if (!requireSubscription()) return;
                            setEditEvent(event);
                            setShowCreate(true);
                          }}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            if (!requireSubscription()) return;
                            setDeleteId(event.id);
                          }} className="text-destructive focus:text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
            <p className="text-xs text-muted-foreground">Showing {(adjPage - 1) * evtPageSize + 1}–{Math.min(adjPage * evtPageSize, list.length)} of {list.length}</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Rows per page:</span>
              <Select value={String(evtPageSize)} onValueChange={(v) => { setEvtPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              {totalPages > 1 && (
                <>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjPage === 1} onClick={() => setPage(1)}>«</Button>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjPage === 1} onClick={() => setPage((p) => p - 1)}>‹</Button>
                  <span className="text-xs px-2">{adjPage} / {totalPages}</span>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjPage === totalPages} onClick={() => setPage((p) => p + 1)}>›</Button>
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjPage === totalPages} onClick={() => setPage(totalPages)}>»</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Inline Event Detail Page */}
      {selectedEvent && (
        <>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={closeEventDetail}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Events
            </Button>
          </div>

          <div className="stat-card p-0 overflow-hidden relative">
            <div className="absolute -left-[9999px] -top-[9999px]" aria-hidden="true">
              <QRCodeSVG id="event-qr-share-svg" value={`${window.location.origin}/checkin?event_id=${selectedEvent.id}`} size={320} level="H" />
            </div>

            <div className="absolute top-3 right-3 z-10">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 bg-background/80 backdrop-blur-sm shadow-sm">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => {
                    if (!requireSubscription()) return;
                    setEditEvent(selectedEvent);
                    setShowCreate(true);
                  }}>
                    <Pencil className="w-4 h-4 mr-2" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => {
                    if (!requireSubscription()) return;
                    setDeleteId(selectedEvent.id);
                  }} className="text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Three-column layout: Left = image, Middle = info, Right = QR + attendees */}
            <div className="grid grid-cols-1 sm:grid-cols-[240px_1fr_220px] min-h-[360px]">
              {/* Left column: tall image or placeholder */}
              <div className="relative h-64 sm:h-full">
                {selectedEvent.flyer_url ? (
                  <img src={selectedEvent.flyer_url} alt="Event flyer" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-muted/50 flex flex-col items-center justify-center gap-2">
                    <ImageIcon className="w-12 h-12 text-muted-foreground/40" />
                    <span className="text-xs text-muted-foreground/50 font-medium">No image</span>
                  </div>
                )}
              </div>

              {/* Middle column: title + info */}
              <div className="p-5 space-y-4">
                <div>
                  <h2 className="text-2xl font-heading font-bold text-foreground">{selectedEvent.name}</h2>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Badge variant="secondary">{selectedEvent.event_type}</Badge>
                  </div>
                </div>

                {/* Event details */}
                <div className="space-y-1.5 text-sm">
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <CalendarDays className="w-4 h-4" />
                    {format(parseISO(selectedEvent.event_date), "EEEE, MMMM d, yyyy")}
                  </p>
                  {selectedEvent.start_time && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      {selectedEvent.start_time.slice(0, 5)}{selectedEvent.end_time && ` – ${selectedEvent.end_time.slice(0, 5)}`}
                      <span className="text-xs opacity-60">{Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(new Date()).find(p => p.type === "timeZoneName")?.value}</span>
                    </p>
                  )}
                  {selectedEvent.location && (
                    <p className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4" /> {selectedEvent.location}
                    </p>
                  )}
                </div>

                {selectedEvent.description && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Description</p>
                    <p className="text-sm text-foreground">{selectedEvent.description}</p>
                  </div>
                )}
                {(() => {
                  const displayNotes = getDisplayNotes(selectedEvent.notes);
                  if (!displayNotes) return null;

                  return (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Notes</p>
                      <p className="text-sm text-muted-foreground italic">{displayNotes}</p>
                    </div>
                  );
                })()}
              </div>

              {/* Right column: QR + Attendees + Share */}
              <div className="p-5 border-t sm:border-t-0 sm:border-l border-border space-y-4">
                <div className="flex flex-col items-center gap-1 p-3 bg-white border border-border rounded-xl shadow-sm">
                  <QRCodeSVG value={`${window.location.origin}/checkin?event_id=${selectedEvent.id}`} size={96} level="H" />
                  <span className="text-[10px] text-muted-foreground font-medium">Scan to Check-in</span>
                </div>

                {eventCounts[selectedEvent.id] ? (
                  <div
                    className="cursor-pointer hover:bg-muted/40 rounded-lg p-2 transition-colors"
                    onClick={() => navigate(`/attendance/event/${selectedEvent.id}`)}
                    title="View & manage attendees"
                  >
                    <p className="text-sm font-medium text-foreground">
                      {eventCounts[selectedEvent.id].members + eventCounts[selectedEvent.id].visitors} attendees
                      {eventCounts[selectedEvent.id].visitors > 0 && (
                        <span className="text-muted-foreground font-normal"> ({eventCounts[selectedEvent.id].visitors} visitors)</span>
                      )}
                      <span className="text-xs text-primary ml-1.5">View &rarr;</span>
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(eventCounts[selectedEvent.id] as any).names?.slice(0, 3).map((name: string, i: number) => (
                        <div key={i} className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-[11px] font-semibold text-primary">
                          {name.split(" ").map((n: string) => n[0]).join("").slice(0, 2)}
                        </div>
                      ))}
                      {(eventCounts[selectedEvent.id] as any).names?.length > 3 && (
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-[11px] font-semibold text-muted-foreground">
                          +{(eventCounts[selectedEvent.id] as any).names.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="cursor-pointer hover:bg-muted/40 rounded-lg p-2 transition-colors"
                    onClick={() => navigate(`/attendance/event/${selectedEvent.id}`)}
                    title="Check In"
                  >
                    <p className="text-sm text-muted-foreground">
                      No attendees yet <span className="text-xs text-primary ml-1.5">Check In &rarr;</span>
                    </p>
                  </div>
                )}

                <div className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">Public view</p>
                    <Switch
                      checked={selectedEvent.public_view_enabled}
                      disabled={publicViewMutation.isPending}
                      onCheckedChange={(checked) => {
                        if (!requireSubscription()) return;
                        publicViewMutation.mutate({ id: selectedEvent.id, enabled: checked });
                      }}
                      aria-label="Toggle public event view"
                    />
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled={!selectedEvent.public_view_enabled}
                  onClick={() => { setLinkCopied(false); setShowShareDialog(true); }}
                >
                  <Share2 className="w-4 h-4 mr-1.5" /> Share
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  disabled={!selectedEvent.public_view_enabled}
                  onClick={() => window.open(`/event?id=${selectedEvent.id}`, "_blank")}
                >
                  <Eye className="w-4 h-4 mr-1.5" /> View Public Page
                </Button>
              </div>
            </div>
          </div>

          {/* Share Dialog */}
          <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
            <DialogContent className="max-w-lg overflow-hidden">
              <DialogHeader>
                <DialogTitle>Share Event</DialogTitle>
              </DialogHeader>

              <p className="text-sm text-foreground">{selectedEvent.name}</p>

              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium mb-3">Share</p>
                <div className="flex gap-3 overflow-x-auto pb-2 justify-center" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  <button
                    className="flex flex-col items-center gap-1.5"
                    onClick={() => {
                      const url = publicEventUrl(selectedEvent);
                      window.open(`sms:&body=${encodeURIComponent(`Check out ${selectedEvent.name}: ${url}`)}`, "_self");
                    }}
                  >
                    <div className="w-14 h-14 rounded-full bg-[#34C759] flex items-center justify-center text-white">
                      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M12 2C6.477 2 2 5.813 2 10.5c0 2.61 1.39 4.96 3.59 6.54L5 21l3.54-1.96c1.08.3 2.22.46 3.46.46 5.523 0 10-3.813 10-8.5S17.523 2 12 2z"/></svg>
                    </div>
                    <span className="text-[11px] text-muted-foreground">iMessage</span>
                  </button>
                  <button
                    className="flex flex-col items-center gap-1.5"
                    onClick={() => {
                      const url = publicEventUrl(selectedEvent);
                      window.open(`viber://forward?text=${encodeURIComponent(`Check out ${selectedEvent.name}: ${url}`)}`, "_self");
                    }}
                  >
                    <div className="w-14 h-14 rounded-full bg-[#7360F2] flex items-center justify-center text-white">
                      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.416s-.037.977.61 1.177c.777.242 1.234-.5 1.98-1.302.407-.44.972-1.084 1.397-1.58 3.85.326 6.812-.416 7.15-.525.776-.252 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546-.596-.55-3.006-2.3-8.375-2.323 0 0-.395-.025-1.037-.017zm.058 1.693c.545-.004.88.017.88.017 4.542.02 6.717 1.388 7.222 1.846 1.675 1.435 2.53 4.868 1.906 9.897v.002c-.604 4.878-4.174 5.184-4.832 5.395-.28.09-2.882.737-6.153.524 0 0-2.436 2.94-3.197 3.704-.12.12-.26.167-.352.144-.13-.033-.166-.188-.165-.414l.02-4.018c-4.762-1.32-4.485-6.292-4.43-8.895.054-2.604.543-4.738 1.996-6.173 1.96-1.773 5.474-2.018 7.11-2.03zm.38 2.602c-.167 0-.303.135-.304.302 0 .167.133.303.3.305 1.624.01 2.946.537 4.028 1.592 1.073 1.046 1.62 2.468 1.633 4.334.002.167.14.3.307.3.166-.002.3-.138.3-.304-.014-1.984-.618-3.596-1.816-4.764-1.19-1.16-2.692-1.753-4.447-1.765zm-3.96.695c-.19-.032-.4.005-.616.117l-.01.002c-.43.247-.816.562-1.146.932-.002.004-.006.004-.008.008-.267.323-.42.638-.46.948-.008.046-.01.093-.007.14 0 .136.022.27.065.4l.013.01c.135.48.473 1.276 1.205 2.604.42.768.903 1.5 1.446 2.186.27.344.56.673.87.984l.132.132c.31.308.64.6.984.87.686.543 1.418 1.027 2.186 1.447 1.328.733 2.126 1.07 2.604 1.206l.01.014c.13.042.265.064.402.063.046.002.092 0 .138-.008.31-.036.627-.19.948-.46.004 0 .003-.002.008-.005.37-.33.683-.72.93-1.148l.003-.01c.225-.432.15-.842-.18-1.12-.004 0-.698-.58-1.037-.83-.36-.255-.73-.492-1.113-.71-.51-.285-1.032-.106-1.248.174l-.447.564c-.23.283-.657.246-.657.246-3.12-.796-3.955-3.955-3.955-3.955s-.037-.426.248-.656l.563-.448c.277-.215.456-.737.17-1.248-.217-.383-.454-.756-.71-1.115-.25-.34-.826-1.033-.83-1.035-.137-.165-.31-.265-.502-.297zm4.49.88c-.158.002-.29.124-.3.282-.01.167.115.312.282.324 1.16.085 2.017.466 2.645 1.15.63.688.93 1.524.906 2.57-.002.168.13.306.3.31.166.003.305-.13.31-.297.025-1.175-.334-2.193-1.067-2.994-.74-.81-1.777-1.253-3.05-1.346h-.024zm.463 1.63c-.16.002-.29.127-.3.287-.008.167.12.31.288.32.523.028.875.175 1.113.422.24.245.388.62.416 1.164.01.167.15.295.318.287.167-.008.295-.15.287-.317-.03-.644-.215-1.178-.58-1.557-.367-.378-.893-.574-1.52-.607h-.018z"/></svg>
                    </div>
                    <span className="text-[11px] text-muted-foreground">Viber</span>
                  </button>
                  <button
                    className="flex flex-col items-center gap-1.5"
                    onClick={() => {
                      const url = publicEventUrl(selectedEvent);
                      window.open(`https://wa.me/?text=${encodeURIComponent(`Check out ${selectedEvent.name}: ${url}`)}`, "_blank");
                    }}
                  >
                    <div className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center text-white">
                      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                    </div>
                    <span className="text-[11px] text-muted-foreground">WhatsApp</span>
                  </button>
                  <button
                    className="flex flex-col items-center gap-1.5"
                    onClick={() => {
                      const url = publicEventUrl(selectedEvent);
                      window.open(`https://m.me/?link=${encodeURIComponent(url)}`, "_blank");
                    }}
                  >
                    <div className="w-14 h-14 rounded-full bg-[#0099FF] flex items-center justify-center text-white">
                      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8.2l3.131 3.26L19.752 8.2l-6.561 6.763z"/></svg>
                    </div>
                    <span className="text-[11px] text-muted-foreground">Messenger</span>
                  </button>
                  <button
                    className="flex flex-col items-center gap-1.5"
                    onClick={() => {
                      const url = publicEventUrl(selectedEvent);
                      window.open(`mailto:?subject=${encodeURIComponent(selectedEvent.name)}&body=${encodeURIComponent(`Check out this event: ${url}`)}`, "_blank");
                    }}
                  >
                    <div className="w-14 h-14 rounded-full bg-gray-500 flex items-center justify-center text-white">
                      <Mail className="w-7 h-7" />
                    </div>
                    <span className="text-[11px] text-muted-foreground">Email</span>
                  </button>
                  <button
                    className="flex flex-col items-center gap-1.5"
                    onClick={() => {
                      const url = publicEventUrl(selectedEvent);
                      const iframe = `<iframe src="${url}" width="400" height="600" frameborder="0"></iframe>`;
                      navigator.clipboard.writeText(iframe);
                      
                    }}
                  >
                    <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                      <Code2 className="w-7 h-7" />
                    </div>
                    <span className="text-[11px] text-muted-foreground">Embed</span>
                  </button>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <div className="flex items-center rounded-lg border border-border bg-muted/30 overflow-hidden">
                  <span className="text-xs text-muted-foreground truncate min-w-0 px-3 py-3" style={{ maxWidth: 'calc(100% - 80px)' }}>
                    {`${publicEventUrl(selectedEvent).slice(0, 40)}...`}
                  </span>
                  <Button
                    className="shrink-0 h-auto px-5 py-3 rounded-none bg-primary text-primary-foreground font-semibold ml-auto"
                    onClick={() => {
                      navigator.clipboard.writeText(publicEventUrl(selectedEvent));
                      setLinkCopied(true);
                      
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                  >
                    {linkCopied ? "Copied!" : "Copy"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Delete confirmation */}
          <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                <AlertDialogDescription>This will permanently delete the event and all its attendance records.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => {
                  if (!requireSubscription()) return;
                  if (deleteId) deleteMutation.mutate(deleteId);
                }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <EventFormDialog
            open={showCreate}
            onClose={() => { setShowCreate(false); setEditEvent(null); setCreatePreFill(null); }}
            event={editEvent}
            initialDate={createPreFill?.date}
            initialTime={createPreFill?.time}
          />
          {qrEvent && <QRCodeDialog open={!!qrEvent} onClose={() => setQrEvent(null)} eventId={qrEvent.id} eventName={qrEvent.name} />}
        </>
      )}
      {/* Header — hidden when viewing event detail */}
      {!selectedEvent && (<>
      <Tabs defaultValue={searchParams.get("tab") || "upcoming"} onValueChange={tab => { setActiveTab(tab); setSearchParams(prev => { const p = new URLSearchParams(prev); p.set("tab", tab); return p; }); }} className="space-y-4">
        <TabsList className="h-9 rounded-none bg-muted p-1 gap-1">
          <TabsTrigger value="upcoming" className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <CalendarCheck className="w-3.5 h-3.5 mr-1.5" />Upcoming
          </TabsTrigger>
          <TabsTrigger value="past" className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <History className="w-3.5 h-3.5 mr-1.5" />Past
          </TabsTrigger>
          <TabsTrigger value="calendar" className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <CalendarDays className="w-3.5 h-3.5 mr-1.5" />Calendar
          </TabsTrigger>
        </TabsList>

        {/* Non-calendar: filter bar */}
        {activeTab !== "calendar" && (
          <TableFilterBar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search events..."
            searchFirstOnMobile
            mobileControlsFirst
            compactDesktopFilters
            filterGroups={[{ key: "event_type", label: "Ministry", options: uniqueEventTypes, selected: typeFilters, onToggle: toggleTypeFilter, onClear: () => setTypeFilters(new Set()) }]}
            dateRange={{
              start: dateRangeStart,
              end: dateRangeEnd,
              onStartChange: (d) => {
                setDateRangeStart(d ?? undefined);
                setUpcomingPage(1);
                setPastPage(1);
              },
              onEndChange: (d) => {
                setDateRangeEnd(d ?? undefined);
                setUpcomingPage(1);
                setPastPage(1);
              },
              onClear: () => {
                setDateRangeStart(undefined);
                setDateRangeEnd(undefined);
                setUpcomingPage(1);
                setPastPage(1);
              },
            }}
            leadingControls={
              <div className="flex items-center border border-border rounded-lg overflow-hidden">
                <button onClick={() => setViewMode("cards")} className={cn("p-2 transition-colors", viewMode === "cards" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")} title="Card view"><LayoutGrid className="w-4 h-4" /></button>
                <button onClick={() => setViewMode("table")} className={cn("p-2 transition-colors", viewMode === "table" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")} title="Table view"><List className="w-4 h-4" /></button>
              </div>
            }
            extraControls={
              activeTab === "upcoming" ? (
                <Button size="sm" onClick={() => { if (!requireSubscription()) return; setShowCreate(true); }}>
                  {isPreview && <span className="mr-2"></span>}
                  <Plus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">New Event</span>
                </Button>
              ) : null
            }
          />
        )}
        {columnsOpen && viewMode === "table" && activeTab !== "calendar" && (
          <TableColumnsPanel columns={columns.map(c => ({ key: c.key, label: c.label }))} visibleColumns={visibleColumns as Set<string>} onToggle={key => toggleColumn(key as EventColumnKey)} onClose={() => setColumnsOpen(false)} />
        )}

        {/* Calendar Apple-style header — single row */}
        {activeTab === "calendar" && (
          <div className="relative flex items-center h-10">
            {/* Left: Filter + View switcher */}
            <div className="flex items-center gap-3 min-w-0">
              {isMobile ? (
                <>
                  <Button variant="outline" size="sm" className="gap-1.5 text-xs h-9 shrink-0" onClick={() => setCalendarTypeFilterOpen(true)}>
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    {typeFilters.size > 0 && <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-[10px] leading-none ml-0.5">{typeFilters.size}</span>}
                  </Button>
                  <Sheet open={calendarTypeFilterOpen} onOpenChange={setCalendarTypeFilterOpen}>
                    <SheetContent side="bottom" className="rounded-t-3xl px-4 pb-4 pt-5 max-h-[85dvh] overflow-y-auto md:hidden">
                      <SheetHeader className="mb-4 text-left">
                        <SheetTitle className="font-heading text-base">Filters</SheetTitle>
                      </SheetHeader>
                      <div className="rounded-lg border border-border bg-card p-2 shadow-sm">
                        <div className="mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Ministry</div>
                        <div className="space-y-0.5">
                          {uniqueEventTypes.map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => toggleTypeFilter(type)}
                              className="w-full flex min-h-9 items-center gap-2 rounded px-2 py-1 text-left hover:bg-slate-100 transition-colors"
                            >
                              <div className={cn("w-3.5 h-3.5 border-2 rounded-sm flex items-center justify-center shrink-0", typeFilters.has(type) ? "bg-primary border-primary" : "border-border")}>
                                {typeFilters.has(type) && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                              </div>
                              <span className="text-sm truncate">{type}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mt-5 flex items-center gap-2 border-t border-border pt-4">
                        {typeFilters.size > 0 && (
                          <Button variant="ghost" className="h-9 flex-1 text-sm" onClick={() => setTypeFilters(new Set())}>Clear all</Button>
                        )}
                        <Button className="h-9 flex-1 text-sm" onClick={() => setCalendarTypeFilterOpen(false)}>Done</Button>
                      </div>
                    </SheetContent>
                  </Sheet>
                </>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-9 shrink-0">
                      <SlidersHorizontal className="w-3.5 h-3.5" />Filter
                      {typeFilters.size > 0 && <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-[10px] leading-none ml-0.5">{typeFilters.size}</span>}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ministry</div>
                    {uniqueEventTypes.map(type => (
                      <DropdownMenuItem key={type} onSelect={e => { e.preventDefault(); toggleTypeFilter(type); }} className="gap-2">
                        <div className={cn("w-3.5 h-3.5 border-2 rounded-sm flex items-center justify-center shrink-0", typeFilters.has(type) ? "bg-primary border-primary" : "border-border")}>
                          {typeFilters.has(type) && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                        </div>
                        <span className="truncate">{type}</span>
                      </DropdownMenuItem>
                    ))}
                    {typeFilters.size > 0 && (<><div className="border-t border-border my-1" /><DropdownMenuItem onSelect={() => setTypeFilters(new Set())} className="text-muted-foreground text-xs">Clear filters</DropdownMenuItem></>)}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <div className="flex rounded-lg border border-border overflow-hidden shrink-0">
                {(["day", "week", "month", "year"] as const).map(v => (
                  <button key={v} onClick={() => setCalendarView(v)} className={cn("px-4 py-1.5 text-sm font-medium capitalize transition-colors", calendarView === v ? "bg-secondary text-foreground font-semibold" : "bg-background text-muted-foreground hover:bg-secondary")}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Search only */}
            <div className="flex items-center ml-auto w-72 shrink-0">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search" className="pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-lg w-full focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" />
              </div>
            </div>
          </div>
        )}

        {/* Upcoming */}
        <TabsContent value="upcoming">
          {renderEventsList(upcoming, "No upcoming events. Create one to get started!", upcomingPage, setUpcomingPage)}
        </TabsContent>

        {/* Past */}
        <TabsContent value="past">
          {renderEventsList(past, "No past events found.", pastPage, setPastPage)}
        </TabsContent>

        {/* Calendar */}
        <TabsContent value="calendar">
          <div className="relative overflow-x-hidden">
          <div className="stat-card">

            {/* ===== DAY VIEW ===== */}
            {calendarView === "day" && (() => {
              const SLOT_H = 28;
              const START_HOUR = 7;
              const numSlots = 28; // 7:00 AM – 8:30 PM
              const totalH = numSlots * SLOT_H;
              let safeDate = calendarMonth instanceof Date && !isNaN(calendarMonth.getTime()) ? calendarMonth : new Date();
              const dayStr = format(safeDate, "yyyy-MM-dd");
              const dayEvts = eventsByDate[dayStr] || [];

              const parseT = (t: string | null, dh = 9, dm = 0): [number, number] => {
                if (!t) return [dh, dm];
                const p = t.split(":").map(Number);
                return [p[0] ?? dh, p[1] ?? 0];
              };
              const items = dayEvts.map(e => {
                const [sh, sm] = parseT(e.start_time);
                const [eh, em] = e.end_time ? parseT(e.end_time) : [sh + 1, sm];
                const startMins = sh * 60 + sm;
                const endMins = Math.max(eh * 60 + em, startMins + 30);
                return { e, startMins, endMins, col: 0, totalCols: 1 };
              });
              items.sort((a, b) => a.startMins - b.startMins);
              const dcols: (typeof items)[] = [];
              for (const item of items) {
                let placed = false;
                for (let ci = 0; ci < dcols.length; ci++) {
                  if (dcols[ci][dcols[ci].length - 1].endMins <= item.startMins) {
                    dcols[ci].push(item); item.col = ci; placed = true; break;
                  }
                }
                if (!placed) { item.col = dcols.length; dcols.push([item]); }
              }
              for (const item of items) {
                let maxC = item.col;
                for (const o of items) { if (o.startMins < item.endMins && o.endMins > item.startMins) maxC = Math.max(maxC, o.col); }
                item.totalCols = maxC + 1;
              }

              return (
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    {/* Nav row */}
                    <div className="flex items-center justify-between mb-2">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => subDays(m, 1))}><ChevronLeft className="w-4 h-4" /></Button>
                      <span className="text-xl font-bold">{format(safeDate, "MMMM d, yyyy")}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => addDays(m, 1))}><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                    <div className="flex overflow-y-auto max-h-[600px]">
                      {/* Time labels */}
                      <div className="w-12 shrink-0 relative select-none" style={{ height: totalH }}>
                        {Array.from({ length: numSlots }, (_, i) => {
                          if (i % 2 !== 0) return null;
                          const h = START_HOUR + i / 2;
                          const label = h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`;
                          return <div key={i} className="absolute text-[10px] text-muted-foreground text-right pr-2 font-medium leading-none w-full" style={{ top: i * SLOT_H + 2 }}>{label}</div>;
                        })}
                      </div>
                      {/* Event column */}
                      <div
                        className="flex-1 relative border-l border-border/30 cursor-default"
                        style={{ height: totalH }}
                        onDoubleClick={ev => {
                          if (!requireSubscription()) return;
                          const rect = ev.currentTarget.getBoundingClientRect();
                          const slotIdx = Math.floor((ev.clientY - rect.top) / SLOT_H);
                          const h = START_HOUR + Math.floor(slotIdx / 2);
                          const m = (slotIdx % 2) * 30;
                          setCreatePreFill({ date: dayStr, time: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` });
                          setEditEvent(null); setShowCreate(true);
                        }}
                      >
                        {Array.from({ length: numSlots }, (_, i) => (
                          <div key={i} className={cn("absolute w-full", i % 2 === 0 ? "border-b border-border/30" : "border-b border-border/10")} style={{ top: i * SLOT_H, height: SLOT_H }} />
                        ))}
                        {items.map(({ e, startMins, endMins, col, totalCols }) => {
                          const top = (startMins - START_HOUR * 60) / 30 * SLOT_H;
                          const height = Math.max(SLOT_H, (endMins - startMins) / 30 * SLOT_H);
                          const wPct = 100 / totalCols;
                          return (
                            <Popover key={e.id} open={popoverEvent?.id === e.id} onOpenChange={open => setPopoverEvent(open ? e : null)}>
                              <PopoverTrigger asChild>
                                <button
                                  style={{ top, height, left: `calc(${col * wPct}% + 2px)`, width: `calc(${wPct}% - 4px)` }}
                                  className={cn("absolute text-left text-xs px-2 py-0.5 rounded font-medium overflow-hidden z-10 border-l-2", getMinistryCalendarTone(e.event_type).surface)}
                                  onClick={ev => ev.stopPropagation()}
                                >
                                  <div className="truncate font-semibold leading-tight">{e.name}</div>
                                  {e.start_time && <div className="text-[10px] opacity-75 leading-tight">{format(parseISO(`2000-01-01T${e.start_time}`), "h:mm a")}{e.end_time && ` – ${format(parseISO(`2000-01-01T${e.end_time}`), "h:mm a")}`}</div>}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent side="right" align="start" className="w-72 p-0 rounded-xl shadow-xl border-border/50">
                                <div className={cn("h-2 rounded-t-xl", getMinistryCalendarTone(e.event_type).bar)} />
                                <div className="p-4 space-y-2">
                                  <h4 className="font-semibold text-sm">{e.name}</h4>
                                  <p className="text-xs text-muted-foreground">{format(parseISO(e.event_date), "EEEE, MMMM d, yyyy")}</p>
                                  {(e.start_time || e.end_time) && <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="w-3 h-3" />{e.start_time && format(parseISO(`2000-01-01T${e.start_time}`), "h:mm a")}{e.end_time && ` – ${format(parseISO(`2000-01-01T${e.end_time}`), "h:mm a")}`}</div>}
                                  {e.location && <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="w-3 h-3" />{e.location}</div>}
                                  {e.description && <p className="text-xs text-muted-foreground border-t pt-2 mt-2">{e.description}</p>}
                                  <div className="flex gap-2 pt-2 border-t">
                                    <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={() => { setPopoverEvent(null); openEventDetail(e); }}><Eye className="w-3 h-3 mr-1" />Details</Button>
                                    <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={() => { setPopoverEvent(null); navigate(`/attendance/event/${e.id}`); }}><CalendarCheck className="w-3 h-3 mr-1" />Attendance</Button>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {/* Right: Month calendar and event list */}
                  <div className="w-full md:w-72 flex flex-col gap-3 md:mt-0 shrink-0">
                    <div className="rounded-lg bg-background">
                      <Calendar mode="single" selected={safeDate}
                        onSelect={d => { if (d instanceof Date && !isNaN(d.getTime())) setCalendarMonth(d); else setCalendarMonth(new Date()); }}
                        className="w-full"
                        modifiers={{ hasEvents: (date) => !!(eventsByDate[format(date, "yyyy-MM-dd")]?.length) }}
                        modifiersClassNames={{ hasEvents: "after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-primary after:content-[''] relative" }}
                      />
                    </div>
                    <div className="rounded-lg border border-border bg-background p-3">
                      <h4 className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wide mb-2">{format(safeDate, "MMM d, yyyy")}</h4>
                      {dayEvts.length === 0 && <p className="text-xs text-muted-foreground">No events</p>}
                      <ul className="space-y-1.5">
                        {dayEvts.map(e => (
                          <li key={e.id} className="flex items-center gap-1.5">
                            <div className={cn("w-1.5 h-1.5 rounded-full shrink-0", getMinistryCalendarTone(e.event_type).dot)} />
                            <div className="flex-1 min-w-0">
                              <div className="truncate text-xs font-medium text-foreground">{e.name}</div>
                              {e.start_time && <div className="text-[10px] text-muted-foreground">{format(parseISO(`2000-01-01T${e.start_time}`), "h:mm a")}{e.end_time && ` – ${format(parseISO(`2000-01-01T${e.end_time}`), "h:mm a")}`}</div>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ===== WEEK VIEW ===== */}
            {calendarView === "week" && (() => {
              const SLOT_H = 28;
              const START_HOUR = 7;
              const numSlots = 28;
              const totalH = numSlots * SLOT_H;
              const weekStart = startOfWeek(calendarMonth);
              const weekEnd = endOfWeek(calendarMonth);
              const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
              const weekEvts = weekDays.flatMap(d => eventsByDate[format(d, "yyyy-MM-dd")] || []);

              const parseT = (t: string | null, dh = 9, dm = 0): [number, number] => {
                if (!t) return [dh, dm];
                const p = t.split(":").map(Number);
                return [p[0] ?? dh, p[1] ?? 0];
              };
              const layoutDayEvts = (evts: Event[]) => {
                const items = evts.map(e => {
                  const [sh, sm] = parseT(e.start_time);
                  const [eh, em] = e.end_time ? parseT(e.end_time) : [sh + 1, sm];
                  const startMins = sh * 60 + sm;
                  const endMins = Math.max(eh * 60 + em, startMins + 30);
                  return { e, startMins, endMins, col: 0, totalCols: 1 };
                });
                items.sort((a, b) => a.startMins - b.startMins);
                const wcols: (typeof items)[] = [];
                for (const item of items) {
                  let placed = false;
                  for (let ci = 0; ci < wcols.length; ci++) {
                    if (wcols[ci][wcols[ci].length - 1].endMins <= item.startMins) {
                      wcols[ci].push(item); item.col = ci; placed = true; break;
                    }
                  }
                  if (!placed) { item.col = wcols.length; wcols.push([item]); }
                }
                for (const item of items) {
                  let maxC = item.col;
                  for (const o of items) { if (o.startMins < item.endMins && o.endMins > item.startMins) maxC = Math.max(maxC, o.col); }
                  item.totalCols = maxC + 1;
                }
                return items;
              };

              return (
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Nav row */}
                    <div className="flex items-center justify-between mb-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => subWeeks(m, 1))}><ChevronLeft className="w-4 h-4" /></Button>
                      <span className="text-xl font-bold">{format(calendarMonth, "MMMM yyyy")}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => addWeeks(m, 1))}><ChevronRight className="w-4 h-4" /></Button>
                    </div>
                    {/* Day headers */}
                    <div className="grid border-b border-border mb-0" style={{ gridTemplateColumns: "48px repeat(7, 1fr)" }}>
                      <div />
                      {weekDays.map(d => {
                        const isToday = isSameDay(d, new Date());
                        return (
                          <div key={d.toISOString()} className="text-center py-1.5">
                            <div className="text-[10px] text-muted-foreground uppercase">{format(d, "EEE")}</div>
                            <button onClick={() => { setCalendarMonth(d); setCalendarView("day"); }} className={cn("text-xs font-semibold w-6 h-6 rounded-full inline-flex items-center justify-center", isToday ? "bg-primary text-primary-foreground" : "hover:bg-secondary")}>{format(d, "d")}</button>
                          </div>
                        );
                      })}
                    </div>
                    {/* Scrollable time grid */}
                    <div className="overflow-y-auto max-h-[560px]">
                      <div className="flex">
                        {/* Time labels */}
                        <div className="w-12 shrink-0 relative select-none" style={{ height: totalH }}>
                          {Array.from({ length: numSlots }, (_, i) => {
                            if (i % 2 !== 0) return null;
                            const h = START_HOUR + i / 2;
                            const label = h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`;
                            return <div key={i} className="absolute text-[10px] text-muted-foreground text-right pr-2 font-medium leading-none w-full" style={{ top: i * SLOT_H + 2 }}>{label}</div>;
                          })}
                        </div>
                        {/* 7 day columns */}
                        {weekDays.map(d => {
                          const dateStr = format(d, "yyyy-MM-dd");
                          const laidOut = layoutDayEvts(eventsByDate[dateStr] || []);
                          return (
                            <div key={dateStr} className="flex-1 relative border-l border-border/30 cursor-default" style={{ height: totalH }}
                              onDoubleClick={ev => {
                                if (!requireSubscription()) return;
                                const rect = ev.currentTarget.getBoundingClientRect();
                                const slotIdx = Math.floor((ev.clientY - rect.top) / SLOT_H);
                                const h = START_HOUR + Math.floor(slotIdx / 2);
                                const m = (slotIdx % 2) * 30;
                                setCreatePreFill({ date: dateStr, time: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` });
                                setEditEvent(null); setShowCreate(true);
                              }}
                            >
                              {Array.from({ length: numSlots }, (_, i) => (
                                <div key={i} className={cn("absolute w-full", i % 2 === 0 ? "border-b border-border/30" : "border-b border-border/10")} style={{ top: i * SLOT_H, height: SLOT_H }} />
                              ))}
                              {laidOut.map(({ e, startMins, endMins, col, totalCols }) => {
                                const top = (startMins - START_HOUR * 60) / 30 * SLOT_H;
                                const height = Math.max(SLOT_H, (endMins - startMins) / 30 * SLOT_H);
                                const wPct = 100 / totalCols;
                                return (
                                  <Popover key={e.id} open={popoverEvent?.id === e.id} onOpenChange={open => setPopoverEvent(open ? e : null)}>
                                    <PopoverTrigger asChild>
                                      <button
                                        style={{ top, height, left: `calc(${col * wPct}% + 1px)`, width: `calc(${wPct}% - 2px)` }}
                                        className={cn("absolute text-left text-[10px] px-1 py-0.5 rounded font-medium overflow-hidden z-10 border-l-2", getMinistryCalendarTone(e.event_type).surface)}
                                        onClick={ev => ev.stopPropagation()}
                                      >
                                        <div className="truncate font-semibold leading-tight">{e.name}</div>
                                        {e.start_time && height > SLOT_H && <div className="text-[9px] opacity-75 leading-tight">{format(parseISO(`2000-01-01T${e.start_time}`), "h:mm a")}</div>}
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent side="right" align="start" className="w-72 p-0 rounded-xl shadow-xl border-border/50">
                                      <div className={cn("h-2 rounded-t-xl", getMinistryCalendarTone(e.event_type).bar)} />
                                      <div className="p-4 space-y-2">
                                        <h4 className="font-semibold text-sm">{e.name}</h4>
                                        <p className="text-xs text-muted-foreground">{format(parseISO(e.event_date), "EEEE, MMMM d, yyyy")}</p>
                                        {(e.start_time || e.end_time) && <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="w-3 h-3" />{e.start_time && format(parseISO(`2000-01-01T${e.start_time}`), "h:mm a")}{e.end_time && ` – ${format(parseISO(`2000-01-01T${e.end_time}`), "h:mm a")}`}</div>}
                                        {e.location && <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><MapPin className="w-3 h-3" />{e.location}</div>}
                                        {e.description && <p className="text-xs text-muted-foreground border-t pt-2 mt-2">{e.description}</p>}
                                        <div className="flex gap-2 pt-2 border-t">
                                          <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={() => { setPopoverEvent(null); openEventDetail(e); }}><Eye className="w-3 h-3 mr-1" />Details</Button>
                                          <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={() => { setPopoverEvent(null); navigate(`/attendance/event/${e.id}`); }}><CalendarCheck className="w-3 h-3 mr-1" />Attendance</Button>
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  {/* Right: Mini calendar + week events list */}
                  <div className="w-full md:w-72 flex flex-col gap-3 shrink-0">
                    <div className="rounded-lg bg-background">
                      <Calendar mode="single" selected={calendarMonth}
                        onSelect={d => { if (d instanceof Date && !isNaN(d.getTime())) setCalendarMonth(d); }}
                        className="w-full"
                        modifiers={{ hasEvents: (date) => !!(eventsByDate[format(date, "yyyy-MM-dd")]?.length) }}
                        modifiersClassNames={{ hasEvents: "after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-primary after:content-[''] relative" }}
                      />
                    </div>
                    <div className="rounded-lg border border-border bg-background p-3">
                      <h4 className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wide mb-2">{format(weekStart, "MMM d")} – {format(weekEnd, "MMM d, yyyy")}</h4>
                      {weekEvts.length === 0 && <p className="text-xs text-muted-foreground">No events this week</p>}
                      <ul className="space-y-1.5">
                        {weekEvts.map(e => (
                          <li key={e.id} className="flex items-center gap-1.5">
                            <div className={cn("w-1.5 h-1.5 rounded-full shrink-0", getMinistryCalendarTone(e.event_type).dot)} />
                            <div className="flex-1 min-w-0">
                              <div className="truncate text-xs font-medium text-foreground">{e.name}</div>
                              <div className="text-[10px] text-muted-foreground">{format(parseISO(e.event_date), "EEE MMM d")}{e.start_time && ` · ${format(parseISO(`2000-01-01T${e.start_time}`), "h:mm a")}`}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ===== MONTH VIEW ===== */}
            {calendarView === "month" && (
              <>
                {/* Nav row */}
                <div className="flex items-center justify-between mb-3">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => subMonths(m, 1))}><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="text-xl font-bold">{format(calendarMonth, "MMMM yyyy")}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => addMonths(m, 1))}><ChevronRight className="w-4 h-4" /></Button>
                </div>
                {/* Day headers */}
                <div className="grid grid-cols-7 gap-px mb-1">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                    <div key={d} className="text-center text-xs font-medium text-muted-foreground py-2">{d}</div>
                  ))}
                </div>
                {/* Days grid */}
                <div className="grid grid-cols-7 gap-px">
                  {Array.from({ length: startDay }).map((_, i) => <div key={`empty-${i}`} className="min-h-[80px]" />)}
                  {calendarDays.map(day => {
                    const dateStr = format(day, "yyyy-MM-dd");
                    const dayEvents = eventsByDate[dateStr] || [];
                    const isToday = isSameDay(day, new Date());
                    return (
                      <div
                        key={dateStr}
                        className={cn("min-h-[80px] border border-border/30 rounded-lg p-1.5 transition-colors cursor-pointer", isToday ? "bg-primary/5 border-primary/30" : "hover:bg-secondary/30")}
                        onClick={() => { setCalendarMonth(day); setCalendarView("day"); }}
                      >
                        <span className={cn("text-xs font-medium", isToday ? "bg-primary text-primary-foreground w-5 h-5 rounded-full inline-flex items-center justify-center" : "text-muted-foreground")}>
                          {format(day, "d")}
                        </span>
                        <div className="mt-1 space-y-0.5">
                          {dayEvents.slice(0, 2).map(e => (
                            <Popover key={e.id} open={popoverEvent?.id === e.id} onOpenChange={(open) => setPopoverEvent(open ? e : null)}>
                              <PopoverTrigger asChild>
                                <button
                                  onClick={(ev) => ev.stopPropagation()}
                                  className={cn("block w-full text-left text-[10px] px-1 py-0.5 rounded truncate", getMinistryCalendarTone(e.event_type).surface)}
                                >
                                  {e.name}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent side="right" align="start" className="w-72 p-0 rounded-xl shadow-xl border-border/50 z-50">
                                <div className={cn("h-2 rounded-t-xl", getMinistryCalendarTone(e.event_type).bar)} />
                                <div className="p-4 space-y-2">
                                  <h4 className="font-semibold text-sm">{e.name}</h4>
                                  <p className="text-xs text-muted-foreground">{format(parseISO(e.event_date), "EEEE, MMMM d, yyyy")}</p>
                                  {(e.start_time || e.end_time) && (
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                      <Clock className="w-3 h-3" />
                                      {e.start_time && format(parseISO(`2000-01-01T${e.start_time}`), "h:mm a")}
                                      {e.end_time && ` – ${format(parseISO(`2000-01-01T${e.end_time}`), "h:mm a")}`}
                                    </div>
                                  )}
                                  {e.location && (
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                      <MapPin className="w-3 h-3" />{e.location}
                                    </div>
                                  )}
                                  {e.description && <p className="text-xs text-muted-foreground border-t pt-2 mt-2">{e.description}</p>}
                                  <div className="flex gap-2 pt-2 border-t">
                                    <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={() => { setPopoverEvent(null); openEventDetail(e); }}>
                                      <Eye className="w-3 h-3 mr-1" />Details
                                    </Button>
                                    <Button size="sm" variant="outline" className="flex-1 text-xs h-7" onClick={() => { setPopoverEvent(null); navigate(`/attendance/event/${e.id}`); }}>
                                      <CalendarCheck className="w-3 h-3 mr-1" />Attendance
                                    </Button>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          ))}
                          {dayEvents.length > 2 && (
                            <span className="text-[10px] text-muted-foreground">+{dayEvents.length - 2} more</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* ===== YEAR VIEW ===== */}
            {calendarView === "year" && (() => {
              const yearStart = startOfYear(calendarMonth);
              const months = eachMonthOfInterval({ start: yearStart, end: endOfYear(calendarMonth) });
              return (
                <>
                  {/* Nav row */}
                  <div className="flex items-center justify-between mb-3">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => subYears(m, 1))}><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="text-xl font-bold">{format(calendarMonth, "yyyy")}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCalendarMonth(m => addYears(m, 1))}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {months.map(month => {
                    const mStart = startOfMonth(month);
                    const mEnd = endOfMonth(month);
                    const days = eachDayOfInterval({ start: mStart, end: mEnd });
                    const pad = mStart.getDay();
                    const isCurrentMonth = isSameMonth(month, new Date());
                    return (
                      <button
                        key={month.toISOString()}
                        onClick={() => { setCalendarMonth(month); setCalendarView("month"); }}
                        className={cn("p-3 rounded-xl border text-left transition-all hover:shadow-md", isCurrentMonth ? "border-primary/40 bg-primary/5" : "border-border/50 hover:border-border")}
                      >
                        <div className="text-sm font-semibold mb-2">{format(month, "MMMM")}</div>
                        <div className="grid grid-cols-7 gap-0">
                          {["S","M","T","W","T","F","S"].map((d, i) => (
                            <div key={i} className="text-[9px] text-muted-foreground text-center">{d}</div>
                          ))}
                          {Array.from({ length: pad }).map((_, i) => <div key={`pad-${i}`} />)}
                          {days.map(day => {
                            const dateStr = format(day, "yyyy-MM-dd");
                            const hasEvents = !!eventsByDate[dateStr]?.length;
                            const isToday = isSameDay(day, new Date());
                            return (
                              <div key={dateStr} className="text-center py-0.5">
                                <span className={cn("text-[10px] inline-block w-4 h-4 leading-4 rounded-full", isToday && "bg-primary text-primary-foreground font-bold", hasEvents && !isToday && "bg-primary/15 text-primary font-semibold")}>
                                  {format(day, "d")}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </button>
                    );
                  })}
                </div>
                </>
              );
            })()}
          </div>

          {/* Search results slide-in panel */}
          <div className={cn("absolute inset-y-0 right-0 w-80 bg-background border-l border-border shadow-2xl overflow-y-auto z-20 transition-transform duration-300 ease-out", search ? "translate-x-0" : "translate-x-full")}>
            <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">Search Results</span>
              <button onClick={() => setSearch("")} className="text-muted-foreground hover:text-foreground transition-colors p-0.5 rounded"><X className="w-4 h-4" /></button>
            </div>
            {(() => {
              const todayStr = format(new Date(), "yyyy-MM-dd");
              const sorted = [...filtered].sort((a, b) => {
                const dc = a.event_date.localeCompare(b.event_date);
                if (dc !== 0) return dc;
                return (a.start_time || "").localeCompare(b.start_time || "");
              });
              type SRGroup = { date: string; events: typeof sorted };
              const groups: SRGroup[] = [];
              for (const e of sorted) {
                const last = groups[groups.length - 1];
                if (last?.date === e.event_date) last.events.push(e);
                else groups.push({ date: e.event_date, events: [e] });
              }
              if (groups.length === 0) return (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">No events found</div>
              );
              const hasPast = groups.some(g => g.date < todayStr);
              let dividerShown = false;
              return groups.map(g => {
                const isFuture = g.date >= todayStr;
                const showDivider = hasPast && isFuture && !dividerShown;
                if (showDivider) dividerShown = true;
                return (
                  <div key={g.date}>
                    {showDivider && (
                      <div className="px-4 py-2 text-xs font-bold text-primary uppercase tracking-widest border-y border-primary/20 bg-primary/5">Today</div>
                    )}
                    <div className="px-4 pt-3 pb-0.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                      {format(parseISO(g.date), "EEEE, MMMM d, yyyy")}
                    </div>
                    {g.events.map(e => (
                      <button key={e.id} className="w-full text-left px-4 py-2 hover:bg-muted/50 flex items-center gap-3 transition-colors" onClick={() => { setSearch(""); openEventDetail(e); }}>
                        <div className={cn("w-2.5 h-2.5 rounded-full shrink-0", getMinistryCalendarTone(e.event_type).dot)} />
                        <span className="flex-1 text-sm font-medium truncate text-foreground">{e.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {e.start_time ? format(parseISO(`2000-01-01T${e.start_time}`), "h:mm a") : "all-day"}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              });
            })()}
          </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Event Detail Dialog — replaced by inline page above */}

      {/* Dialogs */}
      {!selectedEvent && <EventFormDialog open={showCreate} onClose={() => { setShowCreate(false); setEditEvent(null); }} event={editEvent} />}
      {!selectedEvent && qrEvent && <QRCodeDialog open={!!qrEvent} onClose={() => setQrEvent(null)} eventId={qrEvent.id} eventName={qrEvent.name} />}

      {/* Delete confirmation */}
      {!selectedEvent && (
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete the event and all its attendance records.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (!requireSubscription()) return;
              if (deleteId) deleteMutation.mutate(deleteId);
            }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      )}

      {/* Standalone Share Dialog (from card/list/table view) */}
      {shareEvent && (
        <Dialog open={!!shareEvent} onOpenChange={(open) => { if (!open) setShareEvent(null); }}>
          <DialogContent className="max-w-lg overflow-hidden">
            <DialogHeader>
              <DialogTitle>Share Event</DialogTitle>
            </DialogHeader>

            <p className="text-sm text-foreground">{shareEvent.name}</p>

            <div className="border-t border-border pt-4">
              <p className="text-sm font-medium mb-3">Share</p>
              <div className="flex gap-3 overflow-x-auto pb-2 justify-center" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                <button className="flex flex-col items-center gap-1.5" onClick={() => { const url = publicEventUrl(shareEvent); window.open(`sms:&body=${encodeURIComponent(`Check out ${shareEvent.name}: ${url}`)}`, "_self"); }}>
                  <div className="w-14 h-14 rounded-full bg-[#34C759] flex items-center justify-center text-white">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M12 2C6.477 2 2 5.813 2 10.5c0 2.61 1.39 4.96 3.59 6.54L5 21l3.54-1.96c1.08.3 2.22.46 3.46.46 5.523 0 10-3.813 10-8.5S17.523 2 12 2z"/></svg>
                  </div>
                  <span className="text-[11px] text-muted-foreground">iMessage</span>
                </button>
                <button className="flex flex-col items-center gap-1.5" onClick={() => { const url = publicEventUrl(shareEvent); window.open(`viber://forward?text=${encodeURIComponent(`Check out ${shareEvent.name}: ${url}`)}`, "_self"); }}>
                  <div className="w-14 h-14 rounded-full bg-[#7360F2] flex items-center justify-center text-white">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.416s-.037.977.61 1.177c.777.242 1.234-.5 1.98-1.302.407-.44.972-1.084 1.397-1.58 3.85.326 6.812-.416 7.15-.525.776-.252 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546-.596-.55-3.006-2.3-8.375-2.323 0 0-.395-.025-1.037-.017z"/></svg>
                  </div>
                  <span className="text-[11px] text-muted-foreground">Viber</span>
                </button>
                <button className="flex flex-col items-center gap-1.5" onClick={() => { const url = publicEventUrl(shareEvent); window.open(`https://wa.me/?text=${encodeURIComponent(`Check out ${shareEvent.name}: ${url}`)}`, "_blank"); }}>
                  <div className="w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center text-white">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                  </div>
                  <span className="text-[11px] text-muted-foreground">WhatsApp</span>
                </button>
                <button className="flex flex-col items-center gap-1.5" onClick={() => { const url = publicEventUrl(shareEvent); window.open(`https://m.me/?link=${encodeURIComponent(url)}`, "_blank"); }}>
                  <div className="w-14 h-14 rounded-full bg-[#0099FF] flex items-center justify-center text-white">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current"><path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8.2l3.131 3.26L19.752 8.2l-6.561 6.763z"/></svg>
                  </div>
                  <span className="text-[11px] text-muted-foreground">Messenger</span>
                </button>
                <button className="flex flex-col items-center gap-1.5" onClick={() => { const url = publicEventUrl(shareEvent); window.open(`mailto:?subject=${encodeURIComponent(shareEvent.name)}&body=${encodeURIComponent(`Check out this event: ${url}`)}`, "_blank"); }}>
                  <div className="w-14 h-14 rounded-full bg-gray-500 flex items-center justify-center text-white">
                    <Mail className="w-7 h-7" />
                  </div>
                  <span className="text-[11px] text-muted-foreground">Email</span>
                </button>
                <button className="flex flex-col items-center gap-1.5" onClick={() => { const url = publicEventUrl(shareEvent); const iframe = `<iframe src="${url}" width="400" height="600" frameborder="0"></iframe>`; navigator.clipboard.writeText(iframe);  }}>
                  <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                    <Code2 className="w-7 h-7" />
                  </div>
                  <span className="text-[11px] text-muted-foreground">Embed</span>
                </button>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <div className="flex items-center rounded-lg border border-border bg-muted/30 overflow-hidden">
                <span className="text-xs text-muted-foreground truncate min-w-0 px-3 py-3" style={{ maxWidth: 'calc(100% - 80px)' }}>
                  {`${publicEventUrl(shareEvent).slice(0, 40)}...`}
                </span>
                <Button
                  className="shrink-0 h-auto px-5 py-3 rounded-none bg-primary text-primary-foreground font-semibold ml-auto"
                  onClick={() => {
                    navigator.clipboard.writeText(publicEventUrl(shareEvent));
                    setLinkCopied(true);
                    
                    setTimeout(() => setLinkCopied(false), 2000);
                  }}
                >
                  {linkCopied ? "Copied!" : "Copy"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      </>)}
    </div>
  );
}
